import { get } from 'lodash';

import { css, styled } from '../../Utils/style';
import MaterialTypography from '../MaterialTypography/MaterialTypography';

export const SCREEN_VARIANT = {
  standard: {
    sm: {
      content: {
        minHeight: '89.1rem',
        padding: '3.8rem 8.2rem 8rem 8.2rem',
      },
    },
  },
  small: {
    sm: {
      content: {
        minHeight: '55.5rem',
        padding: '3.8rem 8.2rem 4rem',
      },
    },
  },
  progressBar: {
    sm: {
      content: {
        minHeight: '55.5rem',
        padding: '3rem 1.7rem 2rem 1.7rem',
      },
    },
  },
};

export const VARIANTS = {
  progressBar: 'progressBar',
  standard: 'standard',
  small: 'small',
};

export const Wrapper = styled('div', { name: 'ScreenWrapper', slot: 'root' })`
  ${({ $backgroundImage }) =>
    $backgroundImage && $backgroundImage.mobile
      ? `background-image: url(${$backgroundImage.mobile}); background-position: top center; background-size: cover; background-repeat: no-repeat;`
      : ''}

  padding-bottom: env(safe-area-inset-bottom);

  .ScreenWrapper__inner {
    background-color: #fff;
    ${({ $screenWrapperBackgroundImage }) =>
      $screenWrapperBackgroundImage && $screenWrapperBackgroundImage.mobile
        ? `background-image: url(${$screenWrapperBackgroundImage.mobile}); background-position: center; background-size: cover; background-repeat: no-repeat;`
        : ''}
    max-width: ${({ $maxWidth }) => $maxWidth ?? '100%'};
    margin: 0 auto;
    box-shadow: 0 3px 20px #00000014;
    min-height: calc(
      100vh -
        ${(props) =>
          !props.$isAppBarHidden ? get(props, 'theme.components.MuiToolbar.styleOverrides.root.height', '0px') : '0px'}
    );
    display: flex;
    flex-direction: column;

    @supports (height: 100svh) {
      min-height: calc(
        100svh -
          ${(props) =>
            !props.$isAppBarHidden
              ? get(props, 'theme.components.MuiToolbar.styleOverrides.root.height', '0px')
              : '0px'}
      );
    }

    @supports (height: 100dvh) {
      min-height: calc(
        100dvh -
          ${(props) =>
            !props.$isAppBarHidden
              ? get(props, 'theme.components.MuiToolbar.styleOverrides.root.height', '0px')
              : '0px'}
      );
    }
  }

  .ScreenWrapper__top-bar {
    margin-bottom: 1.6rem;
    text-align: ${({ theme }) => (theme.direction === 'rtl' ? 'right' : 'left')};

    .MaterialBack {
      display: flex;
      background: none;
      text-transform: none;
      text-decoration: underline;
      padding: 0;
      min-width: 0;

      .MuiTouchRipple-root {
        display: none;
      }
    }
  }

  .ScreenWrapper__top-image {
    max-width: 140px;
    margin: 3.8rem auto;
    width: 100%;
    aspect-ratio: 1 / 1;
  }

  .ScreenWrapper__header,
  .ScreenWrapper__custom-header {
    ${({ $isFinalScreen }) => ($isFinalScreen ? 'margin-bottom: 4.4rem;' : 'margin-bottom: 1.6rem;')}

    .ScreenWrapper__header__subtitle {
      margin-top: 1.6rem;
      white-space: break-spaces;
    }
  }

  .ScreenWrapper__content {
    ${(props) => (props.$contentFullWidth && !props.$fullWidth ? 'margin: 0 -1.6rem;' : '')}

    .widget {
      margin-bottom: ${(props) => props.theme.spacing(2)};
    }
  }

  .ScreenWrapper__inner-container {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding-top: ${(props) => (props.$hasBackButton || props.$isFinalScreen ? '1.6rem' : '5.1rem')};
    padding-bottom: 2.7rem;
    padding-left: ${(props) => (props.$fullWidth ? '0' : '1.6rem')};
    padding-right: ${(props) => (props.$fullWidth ? '0' : '1.6rem')};
    ${({ $isFinalScreen }) => ($isFinalScreen ? 'text-align: center;' : '')};

    &.progressBar {
      border: ${(props) => `${get(props, 'theme.overrides.ScreenWrapper.border.color')} solid 1px`};
      padding: 3rem 3.5rem;
      border-radius: 3px;
    }
  }

  .ScreenWrapper__progress-bar {
    position: relative;
    background-color: ${(props) => props.theme.palette.progressBar.main};

    .ScreenWrapper__progress-bar-background-image {
      position: absolute;
      display: inline-block;
    }

    .MuiStepper-root {
      padding: 3rem 0;
    }
  }

  .ScreenWrapper__progress-bar:focus,
  .ScreenWrapper__custom-header:focus,
  .ScreenWrapper__header__title:focus,
  .ScreenWrapper__header__subtitle:focus,
  .ScreenWrapper__content:focus {
    outline: none;
  }

  ${(props) => props.theme.breakpoints.up('sm')} {
    padding: 2.4rem 0 3.2rem;

    ${({ $backgroundImage }) =>
      $backgroundImage && $backgroundImage.desktop
        ? `background-image: url(${$backgroundImage.desktop});`
        : 'background-image: none;'}

    .ScreenWrapper__inner {
      min-height: ${({ variant }) =>
        get(SCREEN_VARIANT, `${variant}.sm.content.minHeight`, SCREEN_VARIANT.standard.sm.content.minHeight)};
      ${({ $screenWrapperBackgroundImage }) =>
        $screenWrapperBackgroundImage && $screenWrapperBackgroundImage.desktop
          ? `background-image: url(${$screenWrapperBackgroundImage.desktop}); background-position: center; background-size: cover; background-repeat: no-repeat;`
          : 'background-image: none;'}
      border-radius: 0.8rem;
      padding: ${({ variant }) =>
        variant === VARIANTS.progressBar
          ? get(SCREEN_VARIANT, `${variant}.sm.content.padding`, SCREEN_VARIANT.standard.sm.content.padding)
          : ''};

      .ScreenWrapper__top-bar {
        margin-bottom: 3.8rem;
      }

      .ScreenWrapper__footer {
        margin-top: ${(props) => props.theme.spacing(5)};
      }
    }

    .ScreenWrapper__inner-container {
      padding: ${({ variant }) =>
        get(SCREEN_VARIANT, `${variant}.sm.content.padding`, SCREEN_VARIANT.standard.sm.content.padding)};
    }
  }
`;

export const ScreenWrapperDisclaimer = styled(MaterialTypography, { name: 'ScreenWrapper', slot: 'disclaimer' })`
  ${(props) => {
    return css`
      text-align: center;

      ul {
        margin-block: auto;
        padding-inline-start: 1.5rem;
        text-align: initial;
      }

      & a {
        color: ${props.theme.palette.text.primary};
      }
    `;
  }}
`;

export const ScreenWrapperActions = styled('div', { name: 'ScreenWrapper', slot: 'actions' })`
  ${(props) => {
    return css`
      flex: 100;
      display: flex;
      justify-content: flex-end;
      flex-direction: column;
      margin-top: ${props.theme.spacing(4.75)};
      gap: ${props.theme.spacing(4.75)}
        ${props.$actionsVariant === 'fixed' && 'position: fixed;left: 0; bottom: 1.6rem; width: 100%;'};
    `;
  }}
`;

export const ScreenWrapperDrawerWrapper = styled('span', { name: 'ScreenWrapper' })`
  width: 100%;
`;
